import { Directive, HostListener, ElementRef, Input, Renderer2 } from '@angular/core';
import { debounceTime } from 'rxjs/operators';
import { Subject } from 'rxjs';

@Directive({
    selector: '[appLtmsTooltip]',
    standalone: true
})
export class LtmsTooltipDirective {
  @Input('appLtmsTooltip') tooltipTitle: string = '';
  @Input() placement: string = 'bottom';
  @Input() delay: string = '500';
  @Input() disableTooltip: boolean = false;
  @Input() tooltipMaxWidth: string = '300px';
  tooltip: HTMLElement | null = null;
  offset = 10;
  private mouseMoveSubject = new Subject<MouseEvent>();

  constructor(private el: ElementRef, private renderer: Renderer2) {
    // Subscribe to mouse move events with a debounce
    this.mouseMoveSubject.pipe(debounceTime(100)).subscribe(event => this.updateTooltipPosition(event));
  }

  @HostListener('mouseenter', ['$event'])
  onMouseEnter(e: MouseEvent) {
    // Show tooltip if not disabled and ellipsis is active
    if (!this.disableTooltip && this.isEllipsisActive(e)) {
      this.show(e);
    }
  }

  @HostListener('mouseleave')
  onMouseLeave() {
    // Hide tooltip on mouse leave
    this.hide();
  }

  @HostListener('click')
  onMouseClick() {
    // Hide tooltip on mouse click
    this.hide();
  }

  @HostListener('mousemove', ['$event'])
  onMouseMove(e: MouseEvent) {
    // Emit mouse move events to the subject
    this.mouseMoveSubject.next(e);
  }

  private updateTooltipPosition(e: MouseEvent) {
    // Update tooltip position based on mouse coordinates
    if (this.tooltip) {
      const hostPos = this.el.nativeElement.getBoundingClientRect();
      this.renderer.setStyle(this.tooltip, 'left', `${hostPos.x + (hostPos.width / 4) + 20}px`);
      this.renderer.setStyle(this.tooltip, 'top', `${hostPos.y + hostPos.height + 10}px`);
    }
  }

  private isEllipsisActive(event: MouseEvent) {
    // Check if the target element has an ellipsis
    const target = event.target as HTMLElement;
    return target && (target.offsetWidth < target.scrollWidth);
  }

  private show(e: MouseEvent) {
    // Create and show the tooltip
    if (!this.tooltip) {
      this.create();
    }
    this.renderer.addClass(this.tooltip, 'ltms-tooltip-show');
    this.updateTooltipPosition(e);
  }

  private hide() {
    // Hide the tooltip
    if (this.tooltip) {
      this.renderer.removeClass(this.tooltip, 'ltms-tooltip-show');
    }
  }

  private create() {
    // Create the tooltip element
    this.tooltip = this.renderer.createElement('span');
    this.renderer.appendChild(this.tooltip, this.renderer.createText(this.tooltipTitle));
    this.renderer.appendChild(document.body, this.tooltip);
    this.renderer.addClass(this.tooltip, 'ltms-tooltip');
    this.renderer.addClass(this.tooltip, 'ltms-custom-tooltip');
    this.renderer.setStyle(this.tooltip, 'max-width', this.tooltipMaxWidth);
  }
}